import { formatDistanceToNow } from 'date-fns';
import { useMemo, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Box,
  DataTable,
  DataTableAction,
  DataTableBody,
  DataTableFooter,
  DataTableLabel,
  EllipsisMenuButton,
  Flex,
  Link,
  Menu,
  MenuContent,
  MenuTrigger,
  Text,
} from '@optiaxiom/react';
import {
  type ColumnDef,
  getCoreRowModel,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table';

import { EmptyPage } from '@frontend/components';
import { CompanionRule } from '@frontend/types/app-companion';

import { getEventTypeLabel, getEventTypeProduct } from '../constants';

type CompanionRulesTableProps = {
  rules: CompanionRule[];
  pagination?: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
  isLoading: boolean;
  error: Error | null;
  onEditRule: (rule: CompanionRule) => void;
  onDeleteRule: (ruleId: string) => void;
  onViewRule: (rule: CompanionRule) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

const formatTimestamp = (timestamp: string) => {
  const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
  const date = new Date(utcTimestamp);

  return formatDistanceToNow(date, { addSuffix: true });
};

export function CompanionRulesTable({
  rules,
  pagination,
  isLoading,
  error,
  onEditRule,
  onDeleteRule,
  onViewRule,
  onPageChange,
  onPageSizeChange,
}: CompanionRulesTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<CompanionRule | null>(null);

  const handleDeleteClick = (rule: CompanionRule) => {
    setRuleToDelete(rule);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (ruleToDelete) {
      onDeleteRule(ruleToDelete.id);
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRuleToDelete(null);
  };

  const paginationState = useMemo(
    () => ({
      pageIndex: (pagination?.page || 1) - 1,
      pageSize: pagination?.page_size || 20,
    }),
    [pagination?.page, pagination?.page_size]
  );

  const columns: Array<ColumnDef<CompanionRule>> = useMemo(
    () => [
      {
        id: 'event',
        header: 'Event',
        accessorKey: 'event_type',
        cell: ({ row }) => (
          <Flex flexDirection="column" gap="4">
            <DataTableLabel>
              <DataTableAction asChild primary>
                <Link cursor="pointer" onClick={() => onViewRule(row.original)} appearance="subtle">
                  <Text truncate fontSize="md">
                    {getEventTypeLabel(row.original.event_type)}
                  </Text>
                </Link>
              </DataTableAction>
            </DataTableLabel>
            {getEventTypeProduct(row.original.event_type) && (
              <Text color="fg.secondary" fontSize="sm">
                {getEventTypeProduct(row.original.event_type)}
              </Text>
            )}
            {row.original.description && (
              <Text fontSize="sm" color="fg.tertiary">
                {row.original.description}
              </Text>
            )}
          </Flex>
        ),
      },
      {
        id: 'created_at',
        header: 'Creation Time',
        accessorKey: 'created_at',
        cell: ({ row }) => (
          <Flex flexDirection="column" gap="4">
            <Text fontSize="sm" color="fg.secondary">
              {formatTimestamp(row.original.created_at)}
            </Text>
          </Flex>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Menu
            options={[
              {
                label: 'Edit',
                intent: 'neutral',
                execute: () => onEditRule(row.original),
              },
              {
                label: 'Delete',
                intent: 'danger',
                execute: () => handleDeleteClick(row.original),
              },
            ]}
          >
            <DataTableAction asChild visible="always">
              <MenuTrigger asChild>
                <EllipsisMenuButton appearance="subtle" aria-label="Actions" />
              </MenuTrigger>
            </DataTableAction>
            <MenuContent />
          </Menu>
        ),
      },
    ],
    [onEditRule, onViewRule]
  );

  const table = useReactTable({
    columns,
    data: rules,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: pagination?.total_count || 0,
    pageCount: pagination?.total_pages || 0,
    state: {
      pagination: paginationState,
    },
    onPaginationChange: updater => {
      let newPagination: PaginationState;
      if (typeof updater === 'function') {
        newPagination = updater(paginationState);
      } else {
        newPagination = updater;
      }
      if (newPagination.pageIndex !== paginationState.pageIndex) {
        onPageChange(newPagination.pageIndex + 1);
      }
      if (newPagination.pageSize !== paginationState.pageSize) {
        onPageSizeChange(newPagination.pageSize);
      }
    },
  });

  if (isLoading) {
    return (
      <DataTable table={table}>
        <DataTableBody loading />
        <DataTableFooter showPageSizeOptions />
      </DataTable>
    );
  }

  if (error) {
    return <EmptyPage title="Failed to load rules" description="Failed to load rules" />;
  }

  // Show empty state if no rules and not loading
  if (!isLoading && rules.length === 0) {
    return (
      <Flex justifyContent="center" alignItems="center" py="32">
        <Text fontSize="sm" color="fg.tertiary">
          No rules found. Companion rules will appear here as they are created.
        </Text>
      </Flex>
    );
  }

  return (
    <Box>
      <DataTable maxH="lg" table={table}>
        <DataTableBody loading={isLoading} />
        <DataTableFooter showPageSizeOptions />
      </DataTable>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>Delete Rule</AlertDialogHeader>
          <AlertDialogBody>
            <Box>
              Are you sure you want to delete &quot;
              <strong>{ruleToDelete ? getEventTypeLabel(ruleToDelete.event_type) : ''}</strong>
              &quot;? This action cannot be undone.
            </Box>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} appearance="danger">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
}
